// Every doorstep photo a run published, collected for the operator.
//
// §20.7 splits this three ways and the split is the point: the driver
// seals the photo, every link-holder can see *that* one exists and check
// the commitment, and only a holder of the run seed can open it. This
// console holds one because every job is sealed to it as well as to the
// phone, so it is the one party that can put the picture on a screen.
//
// Collected rather than clicked. A dispatcher settling a complaint is
// looking at one job and wants what the round produced, not a row of
// buttons to press one at a time — and the fetch is idempotent,
// hash-addressed and bounded by what the run's own accumulator vouches
// for, so there is nothing to be careful about except doing it once.
//
// **Nothing here is cached to disk.** The bytes are somebody's doorstep,
// they live in an object URL for as long as the screen is open, and they
// are handed back when it closes. A console that quietly accumulated
// photographs of houses would be the surveillance archive this product
// exists to not build.

import { useCallback, useEffect, useRef, useState } from "react";
import type { FollowHandle } from "@wayfind/core";
import type { Dispatcher } from "./dispatch";
import type { StoredJob } from "./state";

export interface ProofPhoto {
  /** 1-based position in the plan — which door this is. */
  stopIndex: number;
  /** An object URL for the opened image, live while this screen is. */
  url: string;
  /** The commitment the driver signed, for reading down a phone. */
  commitment: string;
}

export interface ProofCollection {
  photos: ProofPhoto[];
  collecting: boolean;
  /** Why nothing came back, in words an operator can act on. */
  problem: string | null;
  /** Commitments the chain vouched for but nobody would hand over. */
  missing: number;
  collect(): void;
}

/**
 * Collects the photos for one job.
 *
 * Runs itself once a run has published a photo chain and stops there —
 * re-fetching on every record would pull blobs off a driver's phone
 * every few seconds for a picture that has not changed.
 */
export function useDeliveryProofs({
  job,
  proof,
  dispatcher,
  consolePrivateKeyHex,
  chainHead
}: {
  job: StoredJob;
  proof: FollowHandle["proof"] | null;
  dispatcher: Dispatcher | null;
  consolePrivateKeyHex: string;
  /** §20.7.1's accumulator, from the newest record. Null: nothing to ask for. */
  chainHead: string | null;
}): ProofCollection {
  const [photos, setPhotos] = useState<ProofPhoto[]>([]);
  const [collecting, setCollecting] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [missing, setMissing] = useState(0);
  // Which head we have already collected for, so a run that keeps
  // publishing position fixes does not re-fetch its photos.
  const done = useRef<string | null>(null);
  const urls = useRef<string[]>([]);

  useEffect(() => () => {
    for (const url of urls.current) URL.revokeObjectURL(url);
    urls.current = [];
  }, []);

  const collect = useCallback(() => {
    if (!proof || !dispatcher || !chainHead || !job.sealedB64 || !consolePrivateKeyHex) return;
    if (done.current === chainHead) return;
    done.current = chainHead;
    setCollecting(true);
    setProblem(null);
    void (async () => {
      try {
        const list = await proof.list(chainHead);
        if (!list.verified.length) {
          setProblem("This run has published no proof this console can verify.");
          return;
        }
        const opened: ProofPhoto[] = [];
        let unreachable = 0;
        for (const entry of list.verified) {
          const sealed = await proof.fetch(entry.commitment);
          if (!sealed) { unreachable++; continue; }
          try {
            const image = await dispatcher.openProofPhoto({
              sealedB64: job.sealedB64,
              consolePrivateKeyHex,
              sealed,
              stopIndex: entry.stopIndex,
              hash: entry.commitment
            });
            const url = URL.createObjectURL(
              new Blob([image.bytes as BlobPart], { type: image.type })
            );
            urls.current.push(url);
            opened.push({ stopIndex: entry.stopIndex, url, commitment: entry.commitment });
          } catch {
            // The bytes matched the commitment and the key did not open
            // them, which on a job this console did not issue is exactly
            // right. Counted with the unreachable rather than thrown:
            // one photo nobody here can open must not hide the others.
            unreachable++;
          }
        }
        setMissing(unreachable);
        setPhotos(opened);
        if (!opened.length) {
          setProblem(
            "The commitments are on the signed records, and nobody reachable is holding the "
            + "bytes. The phone that took them has to be on the mesh to hand them over."
          );
        }
      } catch (error) {
        // A failed collection must be retryable: forget the head so the
        // next attempt is not treated as already done.
        done.current = null;
        setProblem(error instanceof Error ? error.message : String(error));
      } finally {
        setCollecting(false);
      }
    })();
  }, [proof, dispatcher, chainHead, job.sealedB64, consolePrivateKeyHex]);

  useEffect(() => { collect(); }, [collect]);

  return { photos, collecting, problem, missing, collect };
}
